
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Shield, Info, CheckCircle, AlertTriangle, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface InsuranceInformationProps {
  hasInsurance: boolean;
}

const InsuranceInformation: React.FC<InsuranceInformationProps> = ({ hasInsurance }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyDetails = async () => {
    const details = `INSURANCE COVERAGE DETAILS

What's Covered:
• Maximum standard liability is R1,000 per waybill.
• Theft while in carrier's possession.
• Physical damage during handling.
• Coverage up to declared value per waybill.

What's Not Covered:
• Improperly or insufficiently packed items.
• Items described as Dangerous Goods.
• Undeclared glass, ceramics, and fragile items.
• Force Majeure Events.

How to File a Claim:
1. Report the issue to SWIFT within 48 hours.
2. Submit a formal claim within 7 days.
3. Provide the claim form, damage photos, and Waybill.
4. Provide the purchase invoice or proof of value.
5. SWIFT will investigate; its decision is final.

Note: All communication regarding the above must be directed to finance@sendswift.co.za. This is a summary; please refer to SWIFT's Terms and Conditions for full details.`;

    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      toast.success("Insurance details copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  if (!hasInsurance) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-4"
      >
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <h4 className="font-medium text-blue-900">Insurance Coverage Active</h4>
          </div>
          
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Standard liability: Up to R1,000, excluding items listed in our terms.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Maximum coverage: R10,000 per waybill (declared value required).</span>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <span>High-value parcels (over R10,000): Contact SWIFT for assistance.</span>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                <Info className="h-4 w-4 mr-2" />
                How Insurance Works
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl p-6">
              <DialogHeader className="pb-4">
                <DialogTitle className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Insurance Coverage Details
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyDetails}
                    className="ml-4"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 mr-2 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 mr-2" />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">What's Covered</h4>
                  <ul className="space-y-1 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Maximum standard liability is R1,000 per waybill.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Theft while in carrier's possession.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Physical damage during handling.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Coverage up to declared value per waybill.</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">What's Not Covered</h4>
                  <ul className="space-y-1 text-sm">
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
                      <span>Improperly or insufficiently packed items.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
                      <span>Items described as Dangerous Goods.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
                      <span>Undeclared glass, ceramics, and fragile items.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
                      <span>Force Majeure Events.</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">How to File a Claim</h4>
                  <ol className="space-y-1 text-sm list-decimal list-inside">
                    <li>Report the issue to SWIFT within 48 hours.</li>
                    <li>Submit a formal claim within 7 days.</li>
                    <li>Provide the claim form, damage photos, and Waybill.</li>
                    <li>Provide the purchase invoice or proof of value.</li>
                    <li>SWIFT will investigate; its decision is final.</li>
                  </ol>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> All communication regarding the above must be directed to <a href="mailto:finance@sendswift.co.za" className="text-primary underline hover:text-primary/80">finance@sendswift.co.za</a>. This is a summary; please refer to SWIFT's Terms and Conditions for full details.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InsuranceInformation;
